import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  DATE_MONTHS,
  DateMode,
  DateRange,
  daysInMonth,
  formatDateValue,
  getDateYearOptions,
  isDateInRange,
  isDayAllowed,
  isMonthAllowed,
  parseDateParts,
} from "@/lib/entryFields";

interface DateSelectFieldProps {
  id: string;
  label: string;
  required?: boolean;
  mode: DateMode;
  range?: DateRange;
  value: string;
  disabled?: boolean;
  error?: string;
  onChange: (value: string) => void;
}

export function DateSelectField({
  id,
  label,
  required,
  mode,
  range = "both",
  value,
  disabled,
  error,
  onChange,
}: DateSelectFieldProps) {
  const incoming = parseDateParts(value);
  const [year, setYear] = useState(incoming.year);
  const [month, setMonth] = useState(incoming.month);
  const [day, setDay] = useState(incoming.day);

  useEffect(() => {
    if (!value) return;
    const next = parseDateParts(value);
    setYear(next.year);
    setMonth(next.month);
    setDay(next.day);
  }, [value]);

  const yearOptions = getDateYearOptions(range);
  const maxDay = year && month ? daysInMonth(Number(year), Number(month)) : 31;
  const formatted = formatDateValue(year, month, day, mode);
  const rangeMessage = formatted && !isDateInRange(formatted, mode, range)
    ? range === "past"
      ? "That date is in the future. Choose a past date."
      : "That date has already passed. Choose today or a future date."
    : "";

  const update = (next: { year?: string; month?: string; day?: string }) => {
    const nextYear = next.year ?? year;
    let nextMonth = next.month ?? month;
    let nextDay = next.day ?? day;

    if (mode === "full" && nextYear && nextMonth && nextDay) {
      const max = daysInMonth(Number(nextYear), Number(nextMonth));
      if (Number(nextDay) > max) {
        nextDay = "";
      }
    }

    setYear(nextYear);
    setMonth(nextMonth);
    setDay(nextDay);
    onChange(formatDateValue(nextYear, nextMonth, nextDay, mode));
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={`${id}-month`}>
        {label} {required && "*"}
      </Label>
      <div className={cn("grid gap-2", mode === "full" ? "grid-cols-3" : "grid-cols-2")}>
        <Select value={month || undefined} onValueChange={(val) => update({ month: val })} disabled={disabled}>
          <SelectTrigger id={`${id}-month`} className={cn(error && "border-destructive")}>
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {DATE_MONTHS.filter((item) => item.value === month || isMonthAllowed(year, item.value, range)).map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {mode === "full" && (
          <Select value={day || undefined} onValueChange={(val) => update({ day: val })} disabled={disabled}>
            <SelectTrigger id={`${id}-day`} className={cn(error && "border-destructive")}>
              <SelectValue placeholder="Day" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {Array.from({ length: maxDay }, (_, index) => {
                const padded = String(index + 1).padStart(2, "0");
                if (padded !== day && !isDayAllowed(year, month, padded, range)) return null;
                return (
                  <SelectItem key={padded} value={padded}>
                    {index + 1}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
        <Select value={year || undefined} onValueChange={(val) => update({ year: val })} disabled={disabled}>
          <SelectTrigger id={`${id}-year`} className={cn(error && "border-destructive")}>
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {yearOptions.map((item) => (
              <SelectItem key={item} value={item}>
                {item}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {(error || rangeMessage) && <p className="text-xs text-red-500">{error || rangeMessage}</p>}
    </div>
  );
}
